import { useCallback, useEffect, useMemo, useRef } from 'react';

interface RafBatch {
  /** Request a frame if none is queued. Idempotent within the same frame. */
  schedule: () => void;
  /** Cancel a pending frame without running the flush. */
  cancel: () => void;
  /** Run the flush synchronously, cancelling any pending frame first. */
  flush: () => void;
}

/** Coalesces high-frequency state writes into one update per animation frame.
 *  The caller owns the pending buffer (refs); this hook just owns the RAF
 *  handle and invokes the latest `flush` once per frame. The unmount cleanup
 *  cancels any in-flight frame. */
export function useRafBatch(flush: () => void): RafBatch {
  const rafRef = useRef<number | null>(null);
  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  const runFlush = useCallback(() => {
    rafRef.current = null;
    flushRef.current();
  }, []);

  const schedule = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(runFlush);
  }, [runFlush]);

  const cancel = useCallback(() => {
    if (rafRef.current === null) return;
    window.cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const flushNow = useCallback(() => {
    cancel();
    flushRef.current();
  }, [cancel]);

  useEffect(() => () => cancel(), [cancel]);

  // Memoize so consumers can safely put the returned object in dep arrays
  // without forcing their callbacks to rebuild on every render.
  return useMemo(() => ({ schedule, cancel, flush: flushNow }), [schedule, cancel, flushNow]);
}
